import { useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import {
    FlexItemFlexible,
    Responsive,
    SpacingLargeAll,
    SpacingSmallAll,
    useEnterpriseBuildLocation,
} from '@mews-commander/ui';
import { LocalizedText, lt, showSuccessAlert, useDialog, useFeatureFlag } from '@mews/framework';
import { TextKey, FeatureFlag } from '@mews-commander/enums';
import { WithName } from '@mews-commander/types';
import {
    ClickableDiv,
    Container,
    DropDownButton,
    DropdownItem,
    handleRefSplit,
    IconName,
    Stack,
    Title,
} from '@optimus-web/core';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { getCurrentEnterpriseSubscriptionId } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { areNewActionLogsEnabled } from '@mews-commander/legacy-entities/src/ducks/subscriptions';
import { getBill, getBillItemCount, getBillName, isBillVirtual } from '@mews-commander/legacy-entities/src/ducks';
import {
    useCommanderSelector,
    useCommanderThunkDispatch,
    useGainsightCommanderEvents,
    useHoverEffect,
} from '@mews-commander/utils';
import { useRepositoryMutation } from '@mews-commander/data-access';
import { setBills } from '@mews-commander/legacy-entities/src/ducks/bills';
import { BillNameDialog } from '../../../BillNameDialog';
import { BillClassificationTag } from '../../../components/BillClassificationTag';
import { BillClassification, BillingMode } from '../../../constants';
import { TooltippedAutomationIcon } from '../../../../routingRulesNew/components/AutomationIcon';
import { RoutingRuleDetailContainer } from '../../../../routingRulesNew/components/RoutingRuleDetailContainer/RoutingRuleDetailContainer';
import { openModal } from '../../../../app';
import { logBillingAction } from '../../../utils/logBillingAction';
import { DeleteBillButton } from './DeleteBillButton';
import { ItemCountLabel } from './ItemCountLabel';

interface Props {
    onClick(): void;
    billId: string;
    mode?: BillingMode;
    billClassification?: BillClassification;
    accountId?: string;
}

export const BillHeader: React.FC<Props> = ({ onClick, billId, mode, billClassification, accountId }) => {
    const dispatch = useCommanderThunkDispatch();
    const gainsightEvents = useGainsightCommanderEvents();
    const automationIconRef = useRef<HTMLDivElement | null>(null);
    const [showRoutingRuleDetail, setShowRoutingRuleDetail] = useState(false);
    const buildLocation = useEnterpriseBuildLocation();
    const [renameDialogProps, toggleRenameDialog] = useDialog();

    const { mutateAsync } = useRepositoryMutation('bills', 'update', {
        onSuccess: (data) => {
            if (data.bills) dispatch(setBills(data.bills));
            dispatch(logBillingAction('Bill name changed', { name }));
            showSuccessAlert({ description: lt('SaveSuccessMessage') });
        },
    });

    useHoverEffect((hovered) => {
        if (hovered) {
            gainsightEvents.onElementHover({ context: 'BillingAutomation' });
        }
    }, automationIconRef);

    const { ref: inViewRef } = useInView({
        onChange: (inView) => {
            if (inView) {
                gainsightEvents.onElementView({ context: 'BillingAutomation' });
            }
        },
    });

    const currentEnterpriseId = useCurrentEnterpriseId();
    const routingRulesSlice2Enabled = useFeatureFlag(FeatureFlag.routingRulesSlice2Enabled);

    const hasActionLogSubscriptionPrivilege: boolean = useCommanderSelector((state) => {
        const subscriptionId: string = getCurrentEnterpriseSubscriptionId(state);
        return areNewActionLogsEnabled(state, { subscriptionId });
    });

    const name = useCommanderSelector((state) => getBillName(state, { id: billId }));
    const bill = useCommanderSelector((state) => getBill(state, { id: billId }));

    const itemCount = useCommanderSelector((state) =>
        getBillItemCount(state, { billId, enterpriseId: currentEnterpriseId })
    );

    const isVirtual = useCommanderSelector((state) => isBillVirtual(state, { id: billId }));

    const onRename = (e: React.MouseEvent) => {
        e.stopPropagation();

        if (!accountId) return;

        toggleRenameDialog();
    };

    const onSubmitRename = async ({ name: newBillName }: WithName) => {
        await mutateAsync({
            billUpdates: [
                {
                    billId,
                    name: {
                        value: newBillName,
                    },
                },
            ],
            enterpriseId: currentEnterpriseId,
        });
    };

    const onOpenActionLog = (e: React.MouseEvent) => {
        e.stopPropagation();
        dispatch(openModal(buildLocation({ to: 'Bill/ActionLog/$id', params: { id: billId } }).href));
    };

    const showActionLogButton = hasActionLogSubscriptionPrivilege && !isVirtual;

    const showMenu = !isVirtual || showActionLogButton;

    const showClassificationTag =
        !routingRulesSlice2Enabled && mode === BillingMode.AccountBilling && billClassification;

    return (
        <>
            <BillNameDialog
                submitAction={onSubmitRename}
                defaultValues={{
                    name: name || '',
                }}
                submitButtonTextKey={TextKey.Save}
                titleTextKey={TextKey.RenameBill}
                {...renameDialogProps}
            />
            <FlexItemFlexible>
                <ClickableDiv onClick={onClick}>
                    <Responsive Default={SpacingLargeAll} Phone={SpacingSmallAll}>
                        <Stack vertical spacing="200">
                            <Stack distribution="space-between" itemAlignment="center">
                                <Stack spacing="50" itemAlignment="center">
                                    <Title level={3}>{name}</Title>

                                    {bill?.routingRuleId && (
                                        <Container padding={['0', '0', '0', '150']}>
                                            <TooltippedAutomationIcon
                                                tooltip={lt('BillCreatedByAutomation')}
                                                ref={handleRefSplit<HTMLSpanElement>({
                                                    ownRef: automationIconRef,
                                                    forwardedRef: inViewRef,
                                                })}
                                            />
                                        </Container>
                                    )}

                                    {showClassificationTag ? (
                                        <BillClassificationTag billClassification={billClassification} />
                                    ) : null}
                                    {showMenu && (
                                        <DropDownButton
                                            icon={IconName.More}
                                            size="small"
                                            overlayMinWidth={250}
                                            variant="ghost"
                                            data-bill-name-menu
                                        >
                                            {!isVirtual && (
                                                <DropdownItem
                                                    label={<LocalizedText textKey={TextKey.RenameBill} />}
                                                    onClick={onRename}
                                                    data-edit-bill-name
                                                />
                                            )}
                                            {routingRulesSlice2Enabled && bill?.routingRuleId && (
                                                <DropdownItem
                                                    label={
                                                        <LocalizedText
                                                            textKey={TextKey.BillingActionMenuShowAutomation}
                                                        />
                                                    }
                                                    onClick={(e: React.MouseEvent) => {
                                                        e.stopPropagation();
                                                        setShowRoutingRuleDetail(true);
                                                    }}
                                                    data-show-automation
                                                />
                                            )}
                                            {showActionLogButton && (
                                                <DropdownItem
                                                    label={<LocalizedText textKey={TextKey.ActionLog} />}
                                                    onClick={onOpenActionLog}
                                                    data-action-log
                                                />
                                            )}
                                        </DropDownButton>
                                    )}
                                </Stack>
                                <Stack spacing="100" itemAlignment="center">
                                    <ItemCountLabel count={itemCount} />
                                    <DeleteBillButton billId={billId} />
                                </Stack>
                            </Stack>
                        </Stack>
                    </Responsive>
                </ClickableDiv>
            </FlexItemFlexible>
            {routingRulesSlice2Enabled && bill?.routingRuleId && (
                <RoutingRuleDetailContainer
                    opened={showRoutingRuleDetail}
                    onClose={() => setShowRoutingRuleDetail(false)}
                    routingRuleId={bill.routingRuleId}
                    aggregateItems
                />
            )}
        </>
    );
};
